import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search } from 'react-bootstrap-icons';
import { ConsumerLocale } from '../types/ConsumerLocale';
import { useStore } from '../store/store';
import { Image } from 'react-bootstrap';
import loading from '../assets/loading.gif';
import aiDetectLanguage from '../assets/ai-detect-language.png'


const LanguageDropdown = ({ type = "into", label = "Into:", hasValidationError = false }) => {

	const {
		isLoading,
		consumerLocales,
		selectedSourceLocale,
		selectedTargetLocale,
		isFromAutoDetect,
		setSelectedTargetLocale,
		setSelectedSourceLocale,
		setIsFromAutoDetect
	} = useStore((state) => state);

	const languages = useMemo(() => {
		const detectOption = { consumerLocaleId: 'detect', lioLocaleId: -1, name: 'Detect Language ', lioLocaleIso: 'detect', rtl: false };
		return type === 'from' ? [detectOption, ...consumerLocales] : consumerLocales;
	}, [consumerLocales, type]);

	const [selectedLanguage, setSelectedLanguage] = useState('');
	const [selectedLanguageId, setSelectedLanguageId] = useState<number | null>(null);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeLangOptionId, setActiveLangOptionId] = useState<number | null>(null);

	const [filteredLanguages, setFilteredLanguages] = useState(languages);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const optionListRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const filtered = languages.filter(lang => {
				if (type === 'into' && lang.consumerLocaleId === 'detect') {
					return false;
				}
				return lang.name.toLowerCase().includes(searchTerm.toLowerCase());
			}
		);

		const sorted = filtered.sort((a, b) => {
			// if not searching, don't sort to display in alphabetical order
			if (!searchTerm) return 0;

			const aStartsWithSearchTerm = a.name.toLowerCase().startsWith(searchTerm.toLowerCase());
			const bStartsWithSearchTerm = b.name.toLowerCase().startsWith(searchTerm.toLowerCase());
			if (aStartsWithSearchTerm && !bStartsWithSearchTerm) {
				return -1;
			} else if (bStartsWithSearchTerm && !aStartsWithSearchTerm) {
				return 1;
			} else {
				// sort by searchTerm occurence in the search string, closer to start comes first
				const aIndex = a.name.toLowerCase().indexOf(searchTerm.toLowerCase());
				const bIndex = b.name.toLowerCase().indexOf(searchTerm.toLowerCase());

				if (aIndex < bIndex) {
					return -1;
				}
				if (aIndex > bIndex) {
					return 1;
				}

				// sort by source string length, shorter goes first
				return a.name.length - b.name.length;
			}
		});

		setFilteredLanguages(sorted);
	}, [searchTerm, languages, type]);

	// Handle language selection
	const handleLanguageSelect = (language: ConsumerLocale) => {
		setSelectedLanguage(language.name);
		setSelectedLanguageId(language.lioLocaleId!);
		setIsDropdownOpen(false);
		setSearchTerm('');

		// Update the store with the selected language
		if (type === 'from') {
			// For 'detect' option, we use -1 as the lioLocaleId
			setSelectedSourceLocale(language.lioLocaleId);
			if (language.consumerLocaleId === 'detect') {
				setIsFromAutoDetect(true);
			} else {
				setIsFromAutoDetect(false);
			}
		} else {
			setSelectedTargetLocale(language.lioLocaleId);
		}
	};

	const handleSearchChange = (e: any) => setSearchTerm(e.target.value);

	const toggleDropdown = (override?:boolean) => {
		setIsDropdownOpen(prev => override !== undefined ? override : !prev);
		setActiveLangOptionId(null); // reset active option state on open/close

		if (isDropdownOpen && searchInputRef.current) {
			setTimeout(() => {
				searchInputRef?.current?.focus();
			}, 10);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: any) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			} else if (optionListRef.current && optionListRef.current.contains(event.target) && searchInputRef.current) {
				// focus searchbox if the clicked element is in the dropdown menu
				// covers a case where keyboard navigation stops working once searchbox loses focus
				event.preventDefault();
				searchInputRef.current.focus();
			}
		};

		document.addEventListener('mousedown', handleClickOutside, true);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside, true);
		};
	}, []);

	useEffect(() => {
		if (languages.length > 0) {
			if (type === 'from') {
				if (selectedSourceLocale) {
					const language = languages.find(i => i.lioLocaleId === selectedSourceLocale);
					if (language) {
						setSelectedLanguage(language.name);
						setSelectedLanguageId(language.lioLocaleId);
					}
				} else {
					// Default to 'Detect language'
					const detectLanguage = languages.find(i => i.consumerLocaleId === 'detect');
					if (detectLanguage) {
						setSelectedLanguage(detectLanguage.name);
						setSelectedLanguageId(detectLanguage.lioLocaleId);
					}
				}
			} else {
				if (selectedTargetLocale) {
					const language = languages.find(i => i.lioLocaleId === selectedTargetLocale);
					if (language) {
						setSelectedLanguage(language.name);
						setSelectedLanguageId(language.lioLocaleId);
					}
				} else if (languages.length > 0) {
					// Default to English or the first available language when selectedTargetLocale is null
					const defaultEnglish = languages.find(i => i.lioLocaleId === 1);
					if (defaultEnglish) {
						setSelectedLanguage(defaultEnglish.name);
						setSelectedLanguageId(defaultEnglish.lioLocaleId);
					} else {
						setSelectedLanguage(languages[0].name);
						setSelectedLanguageId(languages[0].lioLocaleId);
					}
				}
			}
		}
	}, [type, languages, selectedTargetLocale, selectedSourceLocale]);

	const dropdownId = `dropdown-${type}`;
	const optionListId = `list-${type}`;

	const optionIdBase = `${type}-option-`;
	const optionClass = "lang-option";

	const dropdownControlKeyDownEventHandler = (event: React.KeyboardEvent<HTMLElement>) => {
		// triggers on `keydown` event on dropdown toggle(control) element
		if (event.key === 'Enter' || event.key === " ") {
			// while toggle(control) element is focused, pressing Enter or Space button opens dropdown
			event.preventDefault();
			toggleDropdown(true);
			return;
		}
	};

	const dropdownKeyDownEventHandler = (event: React.KeyboardEvent<HTMLElement>) => {
		// triggers on `keydown` event on dropdown wrapper
		if (event.key === "Tab") {
			// close dropdown on focusing next item in tab sequence
			toggleDropdown(false);
			return;
		}

		if (event.key === "Enter" && searchTerm.length > 0) {
			// select first option from the filtered list
			handleLanguageSelect(filteredLanguages[0]);

			return;
		}

		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			// navigate between options with arrow up/arrow down
			event.preventDefault();

			let nextOptionElement;

			if (activeLangOptionId === null) {
				// option is not selected
				if (event.key === "ArrowDown") {
					// focus first option in the list
					nextOptionElement = document.querySelector(`#${optionListId} .${optionClass}`) as HTMLElement;
				} else {
					// focus last option in the list
					const allOptionElements = document.querySelectorAll(`#${optionListId} .${optionClass}`);
					nextOptionElement = allOptionElements[allOptionElements.length - 1] as HTMLElement;
				}

			} else {
				// option is selected
				if (event.key === "ArrowDown") {
					// focus (n + 1) option in the list
					nextOptionElement = document.querySelector(`#${optionListId} .${optionClass}#${optionIdBase + activeLangOptionId}`)?.nextSibling as HTMLElement;
					if (!nextOptionElement) {
						// currently focusing the last option in the list, focus first element in the list
						nextOptionElement = document.querySelector(`#${optionListId} .${optionClass}`) as HTMLElement;
					}
				} else {
					// focus (n - 1) option in the list
					const allOptionElements = document.querySelectorAll(`#${optionListId} .${optionClass}`);
					let activeElementIndex: number | null = null;

					for (let i = 0; i < allOptionElements.length; i++) {
						const currentEl = allOptionElements[i];
						const currentActiveLangLioId = parseInt(currentEl?.getAttribute("data-lang-id") || "");
						if (currentActiveLangLioId === activeLangOptionId) {
							activeElementIndex = i;
							break;
						}
					}

					if (activeElementIndex !== null) {
						const previousOptionIndex = activeElementIndex - 1 > 0 ? activeElementIndex - 1 : allOptionElements.length - 1; // avoid -1 index

						nextOptionElement = allOptionElements[previousOptionIndex] as HTMLElement;

					} else {
						console.error("Could not find selected option's index");
						return;
					}
				}
			}

			if (nextOptionElement) {
				nextOptionElement?.focus();
				const nextActiveLangLioId = parseInt(nextOptionElement?.getAttribute("data-lang-id") || "");
				if (nextActiveLangLioId) {
					setActiveLangOptionId(nextActiveLangLioId);
				} else {
					console.error("Option element does not contain id");
				}
			} else {
				console.error("Could not find next option element");
			}

			return;
		}

	};

	const optionKeyDownEventHandler = (event: React.KeyboardEvent<HTMLElement>, language: ConsumerLocale) => {
		if (event.key === "Enter" || event.key === " ") {
			// Enter or Space key select currently focused option
			event.preventDefault();
			handleLanguageSelect(language);
		}
	}

	const localeNameLengthTooltipThreshold = 15; // if locale name is less than 15 characters, do not display tooltip

	return (
		<div className="lang-select-container">
			<label
				htmlFor={dropdownId}
				className="lang-select-label"
			>
				{type === 'from' && selectedSourceLocale !== -1 && isFromAutoDetect ? 'Detected:' : label}
			</label>
			<div
				className="lang-dropdown-container placeholder-glow"
				ref={dropdownRef}
				onKeyDown={dropdownKeyDownEventHandler}
			>
				{isLoading && type === 'from' ?
					<div className="lang-dropdown-toggle placeholder">
						<div
							className="position-absolute top-50 start-50 translate-middle"
							style={{ zIndex: 10 }}
						>
							<Image id="loading-gif-dropdown" src={loading} alt={'loading'} />
						</div>
						<span className="text-no-wrap-ellipsis">{selectedLanguage}</span>
					</div>
					: (
					<div
						id={dropdownId}
						className={`lang-dropdown-toggle${hasValidationError ? " lio-has-error" : ""}`}
						onClick={() => toggleDropdown()}
						role="button"
						aria-expanded={isDropdownOpen}
						aria-controls={optionListId}
						tabIndex={0}
						onKeyDown={dropdownControlKeyDownEventHandler}
						title={selectedLanguage.length < localeNameLengthTooltipThreshold ? "" : selectedLanguage}
					>
						<span className="text-no-wrap-ellipsis">{selectedLanguage}</span>
						<span className="lang-dropdown-caret"></span>
					</div>
				)}

				{isDropdownOpen && (
					<div
						ref={optionListRef}
						id={optionListId}
						className={`lang-dropdown-menu ${type === 'from' ? 'start-0' : 'end-0'}`}
						role="listbox"
						aria-label="Languages"
					>
						<div className="lang-search-container">
							<Search className="lang-search-icon" size={16} />
							<input
								ref={searchInputRef}
								type="search"
								className="lang-search-input"
								placeholder="Search languages"
								onChange={handleSearchChange}
								value={searchTerm}
								autoFocus
								aria-label="Search Language options"
							/>
						</div>

						<div className="lang-options-container">
							{filteredLanguages.length > 0 ? (
								filteredLanguages.map((language) => (
									<div
										key={language.consumerLocaleId}
										id={optionIdBase + language.lioLocaleId}
										className={`lang-option text-no-wrap-ellipsis ${selectedLanguageId === language.lioLocaleId ? 'lang-option-selected' : ''}`}
										data-lang-id={language.lioLocaleId}
										role="option"
										tabIndex={-1}
										aria-selected={selectedLanguageId === language.lioLocaleId}
										onClick={() => handleLanguageSelect(language)}
										onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => optionKeyDownEventHandler(event, language)}
										title={language.name.length < localeNameLengthTooltipThreshold ? "" : language.name}
									>
										{language.name}
										{language.consumerLocaleId === 'detect' && <span className="lang-detect-icon">
											<img className="detect-icon-size" src={aiDetectLanguage} alt="detect" />
										</span>}
									</div>
								))
							) : (
								<div className="lang-no-results">No languages found</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default LanguageDropdown;
